import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/auth'
import { apiClient } from '../api/client'
import {
  Button,
  PageLoading,
  PageHeader,
  FilterBar,
  SearchFilter,
  Table,
  Badge,
  Card,
  NoOrganizationMessage,
} from '../components'
import type { FileItem } from '../api/types'

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fi-FI')
}

function getFileIcon(mimeType?: string | null): string {
  if (!mimeType) return '📎'
  if (mimeType.startsWith('image/')) return '🖼️'
  if (mimeType === 'application/pdf') return '📄'
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊'
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝'
  return '📎'
}

export default function FilesPage() {
  const { t } = useTranslation()
  const { activeOrg } = useAuthStore()
  const [files, setFiles] = useState<FileItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('')
  const [uploading, setUploading] = useState(false)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchFiles = useCallback(async () => {
    if (!activeOrg) return
    setLoading(true)
    setError('')
    try {
      const data = await apiClient.get<{ files: FileItem[] }>(
        `/files?organization_id=${activeOrg.id}&limit=100`
      )
      setFiles(data.files || [])
    } catch {
      setError('Tiedostojen lataus epäonnistui')
    } finally {
      setLoading(false)
    }
  }, [activeOrg])

  useEffect(() => {
    fetchFiles()
  }, [fetchFiles])

  const filteredFiles = useMemo(() => {
    return files.filter((file) =>
      filter === '' ||
      file.originalName.toLowerCase().includes(filter.toLowerCase()) ||
      file.filename.toLowerCase().includes(filter.toLowerCase())
    )
  }, [files, filter])

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !activeOrg) return

    setUploading(true)
    setError('')

    const formData = new FormData()
    formData.append('file', file)
    formData.append('organization_id', activeOrg.id)

    try {
      // File upload with FormData - use native fetch for multipart/form-data
      const response = await fetch(`${apiClient['baseUrl']}/files`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        throw new Error(t('files.downloadFailed'))
      }

      await fetchFiles()
    } catch {
      setError(t('files.downloadFailed'))
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDownload = async (file: FileItem) => {
    try {
      const response = await fetch(
        `${apiClient['baseUrl']}/files/${file.id}?organization_id=${activeOrg?.id}`,
        { credentials: 'include' }
      )
      if (!response.ok) {
        throw new Error('Lataus epäonnistui')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.originalName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch {
      setError(t('files.downloadFailed'))
    }
  }

  const handleDelete = async (fileId: string) => {
    if (!confirm(t('files.deleteConfirm'))) return

    try {
      await apiClient.delete(`/files/${fileId}?organization_id=${activeOrg?.id}`)
      await fetchFiles()
    } catch {
      setError(t('files.deleteFailed'))
    }
  }

  const handleProcessOCR = async (fileId: string) => {
    setProcessingId(fileId)
    setError('')

    try {
      await apiClient.post(`/files/${fileId}/process?organization_id=${activeOrg?.id}`, {})
      await fetchFiles()
    } catch {
      setError('OCR-käsittely epäonnistui')
    } finally {
      setProcessingId(null)
    }
  }

  const getOcrStatusBadge = (status: FileItem['ocrStatus']) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Käsitelty</Badge>
      case 'processing':
        return <Badge variant="warning">Käsitellään...</Badge>
      case 'pending':
        return <Badge variant="neutral">Odottaa</Badge>
      case 'failed':
        return <Badge variant="error">Virhe</Badge>
      default:
        return <Badge variant="neutral">-</Badge>
    }
  }

  if (!activeOrg) {
    return <NoOrganizationMessage />
  }

  if (loading) return <PageLoading message="Ladataan tiedostoja..." />

  const columns = [
    {
      key: 'file',
      header: 'Tiedosto',
      render: (file: FileItem) => (
        <div className="flex items-center gap-3">
          <span className="text-2xl">{getFileIcon(file.mimeType)}</span>
          <div>
            <div className="text-sm font-medium text-gray-900">
              {file.originalName}
            </div>
            <div className="text-xs text-gray-500">
              {formatFileSize(file.size)} • {formatDate(file.createdAt)}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Tyyppi',
      render: (file: FileItem) => (
        <span className="text-sm text-gray-600">
          {file.mimeType?.split('/')[1]?.toUpperCase() || file.mimeType || '-'}
        </span>
      ),
    },
    {
      key: 'ocr',
      header: 'OCR Tila',
      align: 'center' as const,
      render: (file: FileItem) => getOcrStatusBadge(file.ocrStatus),
    },
    {
      key: 'actions',
      header: 'Toiminnot',
      align: 'right' as const,
      render: (file: FileItem) => (
        <div className="flex items-center justify-end gap-2">
          {(file.mimeType?.startsWith('image/') || file.mimeType === 'application/pdf') && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPreviewFile(file)}
            >
              👁️ Näytä
            </Button>
          )}
          
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleProcessOCR(file.id)}
            disabled={processingId === file.id || file.ocrStatus === 'processing'}
          >
            {processingId === file.id ? '⏳' : '🤖'} OCR
          </Button>
          
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleDownload(file)}
          >
            ⬇️ Lataa
          </Button>
          
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDelete(file.id)}
          >
            🗑️ Poista
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={`📎 Tiedostot — ${activeOrg.name}`}
        action={
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*,application/pdf,.csv,.xlsx,.xls,.doc,.docx"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? '⏳ ' + t('common.uploading') : '📤 ' + t('common.uploadFile')}
            </Button>
          </>
        }
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <FilterBar>
        <SearchFilter
          value={filter}
          onChange={setFilter}
          placeholder={t('files.searchFiles')}
        />
      </FilterBar>

      <Card>
        {filteredFiles.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-4">📎</div>
            <p className="text-lg font-medium">Ei tiedostoja</p>
            <p className="text-sm">Lataa ensimmäinen tiedosto yllä olevalla napilla</p>
          </div>
        ) : (
          <Table
            data={filteredFiles}
            columns={columns}
            keyExtractor={(file) => file.id}
          />
        )}
      </Card>

      {previewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">{previewFile.originalName}</h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[70vh] flex items-center justify-center bg-gray-100">
              {previewFile.mimeType?.startsWith('image/') ? (
                <img
                  src={`${apiClient['baseUrl']}/files/${previewFile.id}?organization_id=${activeOrg?.id}`}
                  alt={previewFile.originalName}
                  className="max-w-full max-h-[60vh] object-contain"
                />
              ) : previewFile.mimeType === 'application/pdf' ? (
                <iframe
                  src={`${apiClient['baseUrl']}/files/${previewFile.id}?organization_id=${activeOrg?.id}`}
                  className="w-full h-[60vh]"
                  title={previewFile.originalName}
                />
              ) : (
                <p>Esikatselu ei saatavilla tälle tiedostotyypille</p>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPreviewFile(null)}>
                Sulje
              </Button>
              <Button onClick={() => handleDownload(previewFile)}>
                ⬇️ Lataa
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
